import React from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWishlist } from '@/hooks/useWishlist';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface WishlistButtonProps {
  productId: string;
  productName?: string;
  productImage?: string;
  productPrice?: number;
  productUrl?: string;
  variant?: 'icon' | 'full';
  className?: string;
}

export const WishlistButton: React.FC<WishlistButtonProps> = ({
  productId,
  productName,
  productImage,
  productPrice,
  productUrl,
  variant = 'icon',
  className,
}) => {
  const { isInWishlist, toggleItem, isLoggedIn } = useWishlist();
  const inWishlist = isInWishlist(productId);

  const handleToggle = () => {
    if (!isLoggedIn) {
      toast.error('Please log in to use your wishlist');
      return;
    }
    toggleItem({
      product_id: productId,
      product_name: productName ?? null,
      product_image: productImage ?? null,
      product_price: productPrice ?? null,
      product_url: productUrl ?? null,
      quantity: 1,
      notes: null,
    });
    toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist');
  };

  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        className={cn('h-9 w-9', className)}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggle(); }}
        title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Heart className={cn('h-4 w-4 transition-colors', inWishlist && 'fill-destructive text-destructive')} />
      </Button>
    );
  }

  return (
    <Button
      variant={inWishlist ? 'secondary' : 'outline'}
      className={cn('gap-2', className)}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggle(); }}
    >
      <Heart className={cn('h-4 w-4', inWishlist && 'fill-destructive text-destructive')} />
      {inWishlist ? 'In Wishlist' : 'Add to Wishlist'}
    </Button>
  );
};
